/* eslint-disable no-await-in-loop */
/* eslint-disable-next-line no-restricted-syntax */

import i18n from 'i18next'
import { Question } from '../../Models/Question'

const getQuestionSections = (question) => (
    [
        {
            label: i18n.t('q_shortname'),
            type: 'content',
            content: 'shortTitle',
            show: true,
        },
        {
            label: 'Title',
            type: 'content',
            content: 'longTitle',
            show: true,
            element: 'TextArea',
        },
        {
            label: i18n.t('q_instructiontext'),
            type: 'content',
            content: 'instructions',
            show: true,
            hideEmpty: true,
        },
        {
            label: i18n.t('q_content'),
            type: 'content',
            content: 'content',
            show: true,
            hideEmpty: true,
            element: 'TextArea',
        },
        {
            label: i18n.t('q_text_above'),
            type: 'content',
            content: 'aboveText',
            show: true,
            element: 'TextArea',
            hideEmpty: true,
        },
        {
            label: i18n.t('translation.question'),
            type: 'option',
            set: 'optionSet',
            show: question.isSingleAxis(),
        },
        {
            label: i18n.t('translation.option'),
            type: 'subQuestion',
            show: ['FM', 'NF'].includes(question.questionTypeCode),
        },
        {
            label: i18n.t('translation.question_row'),
            type: 'option',
            set: 'optionSetY',
            show: question.isDoubleAxis(),
        },
        {
            label: i18n.t('translation.question_column'),
            type: 'option',
            set: 'optionSetX',
            show: question.isDoubleAxis(),
        },
        {
            label: i18n.t('q_text_below'),
            type: 'content',
            content: 'belowText',
            element: 'TextArea',
            show: true,
            hideEmpty: true,
        },
    ].filter(({ show }) => show)
)

const updateOptiontitle = (option, text, targetLocale) => {
    option.addLangText(targetLocale, { text })
}

const getOptionTitle = (option, locale) => (
    option.langs.has(locale)
        ? option.langs.get(locale).text
        : ''
)

const getContentText = (question, target, locale) => (
    question.content?.[target]?.has(locale)
        ? question.content[target].get(locale).text
        : ''
)

const getDeafultLocale = (locales, defaultLocale, question) => {
    const { metadata } = question
    const { initialLocale } = metadata

    const hasLocale = (locale) => locales.findIndex(({ value }) => value === locale) >= 0

    if (hasLocale(defaultLocale)) return defaultLocale
    if (hasLocale(initialLocale)) return initialLocale
    if (hasLocale('en_GB')) return 'en_GB'

    return locales?.[0]?.value
}

const getWelcomeText = (survey, locale) => {
    const { welcomeContent } = survey.content
    if (welcomeContent.has(locale)) {
        return welcomeContent.get(locale).text
    }

    return ''
}

const setWelcomeText = (survey, locale, text) => {
    survey.content.welcomeContent.set(locale, { text })
}

const getPageContent = (content, locale) => {
    if (content.has(locale)) {
        return content.get(locale).text
    }

    return ''
}

const setPageContent = (content, locale, text) => {
    content.set(locale, { text })
}

const hasContent = (question, content) => (
    Object.hasOwn(question.content, content)
        && question.content[content].values().some(({ text }) => !!text)
)

const getEditableQuestions = (pages) => (
    pages.reduce((map, { questions }) => {
        questions?.forEach(({ question, usedInOtherSurveys }) => {
            const { id, metadata: { oid } } = question
            if (oid >= 6 && !usedInOtherSurveys) {
                map.set(id.toUpperCase(), Question.fromJSON(question.toJSON()))
            }
        })
        return map
    }, new Map())
)

const copyMap = (map) => (
    map.entries().reduce((map, [k, v]) => (
        map.set(k, v)
    ), new Map())
)

const getEditablePages = (survey) => {
    const editablePages = new Map()
    const { exitPages } = survey

    editablePages.set('welcome', copyMap(survey.content.welcomeContent))
    exitPages.forEach(({ id, content }) => {
        editablePages.set(id, copyMap(content))
    })

    return editablePages
}

const saveSurveyQuestions = async (store, survey, editableQuestions, defaultLocale) => {
    const { standardPages } = survey

    const pageQuestions = standardPages.reduce((map, { questions }) => {
        questions?.forEach((pageQuestion) => {
            const { questionId } = pageQuestion
            map.set(questionId.toUpperCase(), pageQuestion)
        })
        return map
    }, new Map())

    for (const [questionId, question] of editableQuestions.entries()) {
        try {
            const title = question.content.getTitleByLocale('shortTitle', defaultLocale, question.metadata.initialLocale)
            const request = {
                contextOid: 7,
                cloneQuestion: false,
                pageQuestionId: pageQuestions.get(questionId).id,
                surveyId: store.survey.id,
                question: question.toJSON(),
            }
            // eslint-disable-next-line no-await-in-loop
            await store.http.put(`/api/question/${questionId}`, JSON.stringify(request))
            store.addNotification({ body: `${title} updated`, level: 'success' })
            pageQuestions.get(questionId).question = question
        } catch (e) {
            console.error(e)
        }
    }
}

const saveWelcomeText = async (store, survey, editablePages) => {
    const welcomeContent = editablePages.get('welcome')
    for (const [locale, { text }] of welcomeContent) {
        const request = {
            locale,
            text,
        }
        try {
            await store.http.put(`/api/survey/${survey.id}/welcomeText`, JSON.stringify(request), store.surveyToken)
            setPageContent(survey.content.welcomeContent, locale, text)
        } catch (e) {
            console.error(e)
        }
    }
}

const saveSurveyExitPages = async (store, survey, editablePages) => {
    const { exitPages } = survey

    for (const page of exitPages) {
        const { id } = page
        const content = editablePages.get(id)

        for (const [locale, { text }] of content) {
            try {
                const request = {
                    locale,
                    text,
                    redirectUrlSettings: page.redirectUrlSettings,
                }
                await store.http.put(`/api/survey/${survey.id}/page/${id}/content`, JSON.stringify(request), store.surveyToken)
                setPageContent(page.content, locale, text)
            } catch (e) {
                console.error(e)
            }
        }
    }
}

const saveSurveyTranslation = async (store, survey, editableQuestions, editablePages, defaultLocale) => {
    console.log('saveSurveyTranslation', editableQuestions)
    await saveSurveyQuestions(store, survey, editableQuestions, defaultLocale)
    await saveWelcomeText(store, survey, editablePages)
    await saveSurveyExitPages(store, survey, editablePages)
}

export default {
    getQuestionSections,
    updateOptiontitle,
    getOptionTitle,
    getContentText,
    getDeafultLocale,
    getWelcomeText,
    setWelcomeText,
    getPageContent,
    setPageContent,
    hasContent,
    getEditableQuestions,
    getEditablePages,
    saveSurveyTranslation,
}
